import { Box } from '@mui/system'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Divider,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  ClickAwayListener,
} from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import { useDispatch } from 'react-redux'
import ConfirmationModalContainer from '../Modals/ConfirmationModalContainer.jsx'
import FinishModalContainer from '../Modals/FinishModalContainer.jsx'
import {
  updateTenantID,
  retrieveUsers,
  resetUserPasswordRequest,
} from '../../redux/features/users/sysAdmin/sysAdminUsersThunk'

const SysAdminUserActionBtn = ({ data, status, statusButtonList, route }) => {
  const history = useHistory()
  const dispatch = useDispatch()

  const [confirmationModalState, setConfirmationModalState] = useState(false)
  const [finishModalState, setFinishModalState] = useState(false)
  const [confirmationData, setConfirmationData] = useState(null)
  const [popperStatus, setPopperStatus] = useState(false)
  const anchorRef = useRef(null)
  const prevOpen = useRef(popperStatus)

  const resetBtnObj = {
    title: 'Reset User Password',
    color: '#000',
    confirmHandler: resetUserPasswordRequest,
    header: 'Reset User Password',
    body: 'Are you sure you want to "Reset" this user password?',
    finish: 'Reset mail has been sent successfully',
    error: 'Could not Reset this user password',
  }

  const handleToggle = () => {
    setPopperStatus(!popperStatus)
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setPopperStatus(false)
  }

  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setPopperStatus(false)
    }
  }

  useEffect(() => {
    if (prevOpen.current === true && popperStatus === false) {
      anchorRef.current.focus()
    }

    prevOpen.current = popperStatus
  }, [popperStatus])

  const changeStatusHandler = (item) => {
    setConfirmationData(item)
    setConfirmationModalState(true)
  }

  const handleNavigation = () => {
    history.push(route)

    dispatch(
      updateTenantID({
        queries: data.company.tenant.id,
      }),
    )
  }

  const finishHandler = () => {
    dispatch(retrieveUsers())
    setFinishModalState(false)
  }

  return (
    <>
      <Box sx={styles.changeStatusContainer}>
        <Button
          ref={anchorRef}
          aria-controls={popperStatus ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          sx={styles.moreActionsButton}
          onClick={handleToggle}>
          <MoreHorizIcon />
        </Button>

        <Popper
          open={popperStatus}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          sx={styles.changeStatusPopper}>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
              <Paper sx={styles.popperPaper} onClick={handleClose}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={popperStatus}
                    id="menu-list-grow"
                    onKeyDown={handleListKeyDown}>
                    <MenuItem onClick={() => handleNavigation()} sx={styles.option}>
                      Edit User Profile
                    </MenuItem>

                    <MenuItem onClick={() => changeStatusHandler(resetBtnObj)} sx={styles.option}>
                      {resetBtnObj.title}
                    </MenuItem>

                    <Divider sx={styles.optionDivider} />

                    <MenuItem disabled sx={styles.optionsTitle}>
                      Change Status To
                    </MenuItem>

                    {statusButtonList
                      ?.filter((item) => item.title !== status?.title)
                      .map((item, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => changeStatusHandler(item)}
                          sx={styles.option}>
                          <FiberManualRecordRoundedIcon style={{ fill: `${item.color}` }} />
                          {item.title}
                        </MenuItem>
                      ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>

      <ConfirmationModalContainer
        modalState={confirmationModalState}
        setModalState={setConfirmationModalState}
        setFinishModalState={setFinishModalState}
        confirmationData={confirmationData}
        confirmHandler={confirmationData?.confirmHandler}
        confirmHandlerQueries={{
          title: confirmationData?.title,
          data,
        }}
      />
      <FinishModalContainer
        modalState={finishModalState}
        modalChangeState={setFinishModalState}
        modalTitle={confirmationData?.title}
        modalMessage={confirmationData?.finish}
        finishHandler={finishHandler}
      />
    </>
  )
}

const styles = {
  changeStatusContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'center',
    marginLeft: 'auto',
    padding: '10px',
    position: { xs: 'absolute', sm: 'unset' },
    top: { xs: '30%', sm: 'unset' },
    right: { xs: '0', sm: 'unset' },

  },
  moreActionsButton: {
    height: { xs: '30px', sm: '40px' },
    minWidth: { xs: '30px', sm: '40px' },
    borderRadius: '12px',
    border: 'solid 2px #e2e8ee',
    backgroundColor: '#f7f9fb',
    color: 'icon.primary',
    padding: '0',
  },
  changeStatusPopper: {
    zIndex: '9',
    minWidth: { xs: '100px', sm: '200px' },
    minHeight: { xs: '20px', sm: 'unset' },
  },
  popperPaper: {
    borderRadius: '12px',
  },
  optionDivider: {
    height: '1px',
    margin: { xs: '6px', sm: '8px 16px' },
    borderRadius: '12px',
    backgroundColor: '#eaeaea',
  },
  option: {
    fontSize: { xs: '10px', sm: '14px' },
    color: '#000000',
    textTransform: 'capitalize',
    padding: { xs: '6px', sm: '6px 16px' },
    minHeight: { xs: '30px', sm: '48px' },
    '& .MuiSvgIcon-root': {
      marginRight: { xs: '4px', sm: '7px' },
      width: { xs: '16px', sm: '1em' },
      height: { xs: '16px', sm: '1em' },
    },
  },
  optionsTitle: {
    fontFamily: 'Avenir',
    fontSize: { xs: '10px', sm: '14px' },
    textTransform: 'capitalize',
    minHeight: { xs: '30px', sm: '48px' },
  },

}

export default SysAdminUserActionBtn
